import { APIService } from "@/services/api";
import { API } from "@/lib/api";
import { ReadAgreement, WriteAgreement } from "@/types/agreement";
import {
  ReadAgreementDetail,
  agreementFileUrlResponse,
  agreementPaperConfirmationFields,
  type AgreementFileDownloadMode,
} from "@/schemas/agreement";

// Create a service for the basic agreement operations (list, create, update, delete)
export const AgreementsService = new APIService<ReadAgreement, WriteAgreement>(
  API.Agreements,
);

// Create a service for agreement details (get by ID)
export const AgreementDetailsService = new APIService<
  { data: ReadAgreementDetail },
  WriteAgreement
>(API.Agreements);

export const fetchAgreementsList = AgreementsService.getList;
export const fetchAgreement = (id: string) =>
  AgreementDetailsService.get(id).then((response) => response.data);
export const createAgreement = AgreementsService.post;
export const updateAgreement = AgreementsService.patch;
export const deleteAgreement = (id: string) => AgreementsService.delete(id);

// New agreement management endpoints
export const sendQuestionnaireLink = (id: string) =>
  AgreementsService.post({}, `${id}/link`);

export const rejectAgreement = (id: string) =>
  AgreementsService.patch(id, {}, "reject");

export const confirmAgreement = (id: string) =>
  AgreementsService.patch(id, {}, "confirm");

export interface AgreementCommentPayloadItem {
  comment: string;
  created_at: string;
}

export const commentAgreement = (
  id: string,
  comments: AgreementCommentPayloadItem[],
) => AgreementsService.patch(id, { comments }, "comment");

export const completeAgreement = (id: string, data: { end_date: string }) =>
  AgreementsService.patch(id, data, "complete");

export const updateAgreementContent = (id: string, amendmentDate: string) =>
  AgreementsService.patch(
    id,
    { amendment_date: amendmentDate },
    "amend-content",
  );

export const updateAgreementData = (
  id: string,
  data: { amendment_date: string },
) => AgreementsService.patch(id, data, "amend-data");

export const updateAgreementStatements = (id: string) =>
  AgreementsService.patch(id, {}, "statements");

export const updateAgreementTaxStatus = (
  id: string,
  data: { amendment_date: string },
) => AgreementsService.patch(id, data, "amend-tax-status");

/**
 * Rodzaj potwierdzenia zapisywany ręcznie (ASMMNT-83). Od etapu 2 tylko Dział Kadr:
 * potwierdzenie rekrutera jest pochodną aktywnych skanów (`POST …/paper-scans/*`),
 * a endpoint `PATCH …/paper-confirmation/recruiter` nie istnieje.
 */
export type AgreementPaperConfirmationKind = "hr";

/** Pola potwierdzeń zwracane przez backend po zapisie i w szczegółach umowy. */
export type { AgreementPaperConfirmationFields } from "@/schemas/agreement";

/** Zapis potwierdzenia; odpowiedź przechodzi przez schemat zod, zanim trafi do cache. */
export const setAgreementPaperConfirmation = (
  id: string,
  kind: AgreementPaperConfirmationKind,
  confirmed: boolean,
) =>
  AgreementsService.patch<{ confirmed: boolean }, unknown>(
    id,
    { confirmed },
    `paper-confirmation/${kind}`,
  ).then((response) => agreementPaperConfirmationFields.parse(response));

/** Odpowiedź `POST …/files/download-url` po walidacji zodem. */
export type { AgreementFileUrlResponse } from "@/schemas/agreement";

export interface AgreementFileUrlRequest {
  /** Ścieżka z `agreement_files[].path` albo `paper_scans[].path` — backend akceptuje tylko te dwa kształty */
  path: string;
  /** Pobranie (`attachment`) albo podgląd (`inline`); oba są rejestrowane jako zdarzenie */
  mode: AgreementFileDownloadMode;
}

/**
 * Prosi backend o krótkotrwały presigned GET do pliku umowy (ASMMNT-83, etap 2).
 * Backend zapisuje zdarzenie pobrania; front nie buduje już adresu `files/agreements/…` sam.
 */
export const requestAgreementFileUrl = (
  id: string,
  request: AgreementFileUrlRequest,
) =>
  AgreementsService.post<AgreementFileUrlRequest, unknown>(
    request,
    `${id}/files/download-url`,
  ).then((response) => agreementFileUrlResponse.parse(response));
